// client/src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signIn, confirmSignIn, signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
const AuthContext = createContext(null);

let _cachedTokenGetter = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // ── 1. Get fresh Cognito access token ────────────────────────
  const getAccessToken = useCallback(async (forceRefresh = false) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    _cachedTokenGetter = getAccessToken;
    return () => { _cachedTokenGetter = null; };
  }, [getAccessToken]);

  // ── 2. checkAuth — verify session + sync DB user ─────────────
  const checkAuth = useCallback(async () => {
    try {
      await getCurrentUser();
      const token = await getAccessToken();
      if (!token) throw new Error('No token');

      const res = await fetch(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Not authenticated');

      const data = await res.json();
      setUser(data);
      return data;
    } catch (err) {
      console.error('[checkAuth]', err.message);
      setUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, [getAccessToken]);

  // ── 3. Request OTP ────────────────────────────────────────────
  // Step A: server ensures user exists in Cognito
  // Step B: Amplify triggers EMAIL_OTP challenge → Cognito sends email via SES
  const requestOTP = useCallback(async (email) => {
    const res = await fetch(`${API_URL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to send OTP');
    }

    let result = await signIn({
      username: email,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
    });

    // Cognito asks to pick a factor when multiple are enabled on the app client
  if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
    result = await confirmSignIn({ challengeResponse: 'EMAIL_OTP' });
  }

    if (result.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      throw new Error('Unexpected auth step: ' + result.nextStep?.signInStep);
    }

    return { success: true };
  }, []);

  // ── 4. Verify OTP ─────────────────────────────────────────────
  // Confirm challenge → Amplify stores tokens → checkAuth upserts DB row
  const verifyOTP = useCallback(async (otp) => {
    const result = await confirmSignIn({ challengeResponse: otp });
    if (!result.isSignedIn) throw new Error('Sign in incomplete');
    return await checkAuth();
  }, [checkAuth]);

  // ── 5. Logout ─────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await signOut();
      fetch(`${API_URL}/auth/logout`, { method: 'POST' }).catch(() => {});
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  }, []);

  // ── 6. Google OAuth — Phase 2 (unchanged) ────────────────────
  const loginWithGoogle = useCallback((mode = 'signin') => {
    const popup = window.open(
      `${API_URL}/auth/google?state=${mode}`,
      'Google Login',
      'width=500,height=600,left=400,top=100'
    );
    const origin = window.location.origin;
    const handleMessage = async (event) => {
      if (event.origin !== origin) return;
      window.removeEventListener('message', handleMessage);
      if (event.data?.type === 'auth-success') {
        const userData = await checkAuth();
        if (userData) {
          window.location.replace(event.data.onboarding ? '/?onboarding=true' : '/');
        } else {
          await new Promise(r => setTimeout(r, 300));
          const retryData = await checkAuth();
          if (retryData) window.location.replace(event.data.onboarding ? '/?onboarding=true' : '/');
        }
      }
      if (event.data?.type === 'auth-error') {
        const { error, redirect } = event.data;
        window.location.replace(`/${redirect}?error=${error}`);
      }
    };
    window.addEventListener('message', handleMessage);
    const timer = setInterval(() => {
      if (popup.closed) { clearInterval(timer); window.removeEventListener('message', handleMessage); }
    }, 500);
  }, [checkAuth]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, setUser, authLoading, requestOTP, verifyOTP, logout, loginWithGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ── fetchWithAuth — unchanged, used by useChat.js ─────────────
export const fetchWithAuth = async (url, options = {}) => {
  const getToken = _cachedTokenGetter;
  const buildHeaders = (token) => ({
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  const token = getToken ? await getToken() : null;
  let res = await fetch(url, { ...options, headers: buildHeaders(token) });

  if (res.status === 401) {
    const freshToken = getToken ? await getToken(true) : null;
    if (freshToken) res = await fetch(url, { ...options, headers: buildHeaders(freshToken) });
    if (res.status === 401) window.location.href = '/signin';
  }

  return res;
};